import { useSyncExternalStore } from "react";

/**
 * One clock for the whole screen, ticking at the precision its consumers actually display.
 *
 * Fifty conversation rows each running their own interval is fifty timers — but one timer at
 * 1Hz driving fifty rows that read "4h 12m" is fifty renders a second to redraw text that
 * changes once a minute. That is the same waste, moved.
 *
 * So a consumer subscribes at the resolution of its own output, and the snapshot is quantised
 * to that resolution. React compares snapshots with Object.is and skips the re-render when the
 * quantised value has not moved, so a minute-resolution row renders once a minute no matter
 * how often the store is notified.
 *
 * The wake is scheduled to the next boundary of the finest resolution anyone needs. When
 * nothing on screen is counting seconds that is one wake per minute, the display flips exactly
 * on the boundary rather than drifting, and the delay is recomputed from the wall clock each
 * time so error cannot accumulate.
 *
 * docs/08-design-system-v2.md §3 P2 and P3.
 */

/** How often a consumer's rendered output can actually change. */
export type Tick = "second" | "minute";

const STEP: Record<Tick, number> = { second: 1_000, minute: 60_000 };

let now = Date.now();

/** Every listener, with the step it needs. */
const listeners = new Map<() => void, number>();
let timer: ReturnType<typeof setTimeout> | null = null;
/** The step the pending wake was armed for; 0 when nothing is scheduled. */
let armed = 0;

function finestStep(): number {
  let step = STEP.minute;
  for (const s of listeners.values()) if (s < step) step = s;
  return step;
}

function wake(): void {
  timer = setTimeout(
    () => {
      now = Date.now();
      // A listener may unsubscribe during notification, so iterate a copy.
      for (const fn of [...listeners.keys()]) fn();
      wake();
    },
    armed - (Date.now() % armed),
  );
}

/** Re-arm only when the finest required step has actually changed. */
function retime(): void {
  const step = listeners.size === 0 ? 0 : finestStep();
  if (step === armed) return;
  if (timer !== null) clearTimeout(timer);
  timer = null;
  armed = step;
  if (step !== 0) wake();
}

/**
 * One subscribe function per step, and one snapshot function per step.
 *
 * Both identities have to be stable across renders: useSyncExternalStore re-subscribes when
 * `subscribe` changes, and warns when `getSnapshot` returns a fresh value every call. Building
 * them per step rather than per render gives both properties, and changing the step — which is
 * what a countdown crossing into its last hour does — correctly re-subscribes.
 */
const subscribeFns = new Map<number, (fn: () => void) => () => void>();
const snapshotFns = new Map<number, () => number>();

function subscribeAt(step: number): (fn: () => void) => () => void {
  const existing = subscribeFns.get(step);
  if (existing) return existing;
  const fn = (listener: () => void): (() => void) => {
    listeners.set(listener, step);
    // Nothing has been keeping `now` fresh while no one was subscribed.
    now = Date.now();
    retime();
    return () => {
      listeners.delete(listener);
      retime();
    };
  };
  subscribeFns.set(step, fn);
  return fn;
}

function snapshotAt(step: number): () => number {
  const existing = snapshotFns.get(step);
  if (existing) return existing;
  const fn = (): number => Math.floor(now / step) * step;
  snapshotFns.set(step, fn);
  return fn;
}

/**
 * The current time, rounded down to the resolution you will render at.
 *
 * There is no default: a caller who has not thought about which one they need is a caller
 * about to re-render a hundred rows a second, or to leave a countdown frozen for a minute.
 */
export function useNow(tick: Tick): number {
  const step = STEP[tick];
  const snapshot = snapshotAt(step);
  return useSyncExternalStore(subscribeAt(step), snapshot, snapshot);
}

/** Test seam. Not exported from the package — see clock.test.ts. */
export const __clock = {
  subscribeAt: (tick: Tick) => subscribeAt(STEP[tick]),
  snapshotAt: (tick: Tick) => snapshotAt(STEP[tick]),
  armedStep: () => armed,
  listenerCount: () => listeners.size,
};
